import { useState } from "react";
import type { MenuItemResponse } from "@/api/lib/db/types";
import Icon from "../../../lib/components/custom/Icon";
import { Image } from "../../../lib/components/custom/Image";
import { Badge } from "../../../lib/components/ui/badge";
import { Button } from "../../../lib/components/ui/button";
import { Card } from "../../../lib/components/ui/card";
import { useApi } from "../../../lib/hooks/api/use-api";
import { ImageSelectionDialog } from "./ImageSelectionDialog";

type MenuItem = MenuItemResponse;

export function AdminItemCard({
	item,
	onEdit,
}: {
	item: MenuItem;
	onEdit: (itemId: number, item: MenuItem) => void;
}) {
	const [imageDialogOpen, setImageDialogOpen] = useState(false);
	const rating = Math.max(0, Math.min(5, Number(item.rating)));

	// Fetch all menu sizes and filter for this item
	const { data: allSizes } = useApi().menu.getSizes();

	// Filter sizes for this menu item and get the lowest price
	const itemSizes =
		allSizes?.filter(
			(size) => size.menuItemId === item.id && size.isAvailable,
		) || [];
	const lowestPrice =
		itemSizes.length > 0
			? Math.min(...itemSizes.map((size) => parseFloat(size.price)))
			: null;

	return (
		<>
			<Card className="relative overflow-visible rounded-2xl p-6 bg-card flex flex-col h-full">
				{/* Content that grows */}
				<div className="flex-1 flex flex-col gap-4">
					<div className="absolute -top-18 right-10 flex justify-center z-10">
						<button
							type="button"
							className="size-36 md:size-40 rounded-full bg-background shadow-md flex items-center justify-center cursor-pointer hover:ring-4 ring-primary/20 transition-all relative group"
							onClick={() => setImageDialogOpen(true)}
						>
							{item.imageUrl ? (
								<Image
									src={item.imageUrl}
									alt={item.name}
									className="size-32 md:size-36 rounded-full object-cover"
								/>
							) : (
								<div className="size-32 md:size-36 rounded-full bg-accent" />
							)}
							<div className="absolute inset-0 bg-black/40 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
								<Icon name="Camera" className="text-white w-8 h-8" />
							</div>
						</button>
					</div>

					{/* Dietary badges */}
					<div className="flex flex-col gap-2 mb-4">
						<div className="flex flex-col gap-1">
							<h4 className="font-semibold text-lg xl:max-w-32">{item.name}</h4>
							<span className="text-sm font-mono text-muted-foreground">
								ID: {item.displayId}
							</span>
						</div>
						<div className="flex items-center gap-2 mt-1">
							<div
								className="text-primary text-sm"
								role="img"
								aria-label={`${rating} out of 5`}
							>
								{Array.from({ length: 5 }).map((_, i) => (
									<span key={i}>{i < rating ? "★" : "☆"}</span>
								))}
							</div>
							<span className="text-xs text-muted-foreground">
								({item.rating.toFixed(1)})
							</span>
						</div>
						{item.description && (
							<p className="text-sm text-muted-foreground mt-2">
								{item.description}
							</p>
						)}
					</div>
				</div>

				{/* Price and Edit Button Row - Fixed at bottom */}
				<div className="flex flex-col gap-4">
					<div className="flex items-center gap-2">
						<Badge className="">{item.category}</Badge>

						<div className="flex items-center gap-2">
							{item.isVegan && (
								<Badge
									variant="secondary"
									className="text-xs bg-green-100 text-green-700 border-green-200"
								>
									🌱 Vegan
								</Badge>
							)}
							{item.isVegetarian && (
								<Badge
									variant="secondary"
									className="text-xs bg-blue-100 text-blue-700 border-blue-200"
								>
									🥬 Vegetarian
								</Badge>
							)}
						</div>
					</div>

					<div className="flex items-center justify-between">
						{lowestPrice && (
							<div className="text-2xl flex flex-col gap-4 font-bold text-primary">
								€{lowestPrice.toFixed(2)}
							</div>
						)}
						{!lowestPrice && <div />} {/* Spacer if no price */}
						<Button
							variant="outline"
							size="sm"
							onClick={() => onEdit(item.id, item)}
							className="ml-auto"
						>
							<Icon name="Settings" className="w-4 h-4 mr-2" />
							Edit
						</Button>
					</div>
				</div>
			</Card>
			<ImageSelectionDialog
				item={item}
				open={imageDialogOpen}
				onOpenChange={setImageDialogOpen}
				onSuccess={() => {}}
			/>
		</>
	);
}
